// ============================================================
// InquiryModal — โมดัลจัดการคำขอนัดดูห้องของ "ห้อง 1 ห้อง"
// ------------------------------------------------------------
// ใช้ร่วมกัน 2 หน้า : /project-room-all (ปุ่มนัดหมายบนการ์ด) และ /sale-inquiries
// ★ ต้องเป็นคอมโพเนนต์เดียว ไม่ก๊อปโค้ดสองที่ — ฟอร์มติดตามมีหลายช่อง
//   ถ้าแยกกันแก้ อีกหน้าจะตกรุ่นเงียบ ๆ
//
// โครง : กางทีละใบ (accordion)
//   ส่วนที่ 1  ข้อมูลจากผู้สนใจ — อ่านอย่างเดียว (มาจากฟอร์มหน้าเว็บสาธารณะ)
//   ส่วนที่ 2  การติดตามของผู้ดูแล — สถานะ / วันนัดชม / วันติดตาม / บันทึกภายใน
//              แล้วกดบันทึกทีเดียวทั้งชุด
// ============================================================

import React, { Component } from "react";
import { PUT, alert, Modal, formatSaleDateTime, formatSaleDate, DateTimePicker, hasPermission } from "./CustomComponent.js";
import {
  INQUIRY_STATUS as INQUIRY_STATUS_KEY,
  INQUIRY_STATUS_LIST,
  INQUIRY_STATUS_STYLE as STATUS_STYLE,
} from "../constants/sale.js";

// ต้องตรงกับ STATUS_LIST ฝั่ง backend (modules/sale-inquiry/sale-inquiry.service.js)
// ★ ค่าและสีป้ายย้ายไป constants/sale.js — export ต่อจากที่นี่ให้หน้าที่ import อยู่เดิมไม่พัง
export const INQUIRY_STATUS = INQUIRY_STATUS_LIST;
export const INQUIRY_STATUS_STYLE = STATUS_STYLE;

// ---- ความเร่งด่วนจาก "วันนัดชมห้อง" (ใช้ร่วมกับหน้ารายการนัดหมาย) ----
// เลขน้อย = ต้องจัดการก่อน
export const URGENCY = { OVERDUE: 0, SOON: 1, NORMAL: 2 };

// ใกล้ถึงวันนัด = ภายใน 2 วัน
export const SOON_DAYS = 2;

// ★ นัดไว้แล้วแต่ยังไม่ปิดงาน = ยังมีงานค้าง เลยวันนัดคือหลุดนัด ต้องเห็นก่อนใคร
//   งานที่ปิดแล้วไม่นับ — จบไปแล้ว ไม่ต้องเตือนซ้ำ
export const appointmentUrgency = (item, now = Date.now()) => {
    if (!item?.appointment_at || (item.status || INQUIRY_STATUS_KEY.NEW) === INQUIRY_STATUS_KEY.CLOSED) return URGENCY.NORMAL;

    const time = new Date(String(item.appointment_at).replace(" ", "T")).getTime();
    if (isNaN(time)) return URGENCY.NORMAL;

    if (time < now) return URGENCY.OVERDUE;
    if (time - now <= SOON_DAYS * 24 * 60 * 60 * 1000) return URGENCY.SOON;
    return URGENCY.NORMAL;
};

// สี + ป้ายของแต่ละระดับ (ใช้ทั้งการ์ดห้องและแถวในโมดัล)
export const URGENCY_STYLE = {
    [URGENCY.OVERDUE]: {
        card: { borderColor: "#dc3545", background: "#fff1f1" },
        badge: { background: "#dc3545", color: "#fff" },
        label: "เกินวันนัดหมาย",
    },
    [URGENCY.SOON]: {
        card: { borderColor: "#f0921e", background: "#fff8e8" },
        badge: { background: "#f0921e", color: "#fff" },
        label: "ใกล้ถึงวันนัด",
    },
};

// ---- ลำดับการติดต่อ ----
// ★ เดิมเป็นช่องพิมพ์ตัวเลขอิสระ ผู้ดูแลต้องคิดเองว่าจะให้เลขอะไร และเลขซ้ำกันได้
//   เปลี่ยนเป็น 3 ระดับตายตัว เลือกเร็วกว่า และยังเรียงคิวได้เหมือนเดิม (เลขน้อย = ตามก่อน)
//   ค่าเก่าที่เป็นเลขอื่นยังใช้เรียงได้ปกติ แค่ไม่ตรงกับปุ่มไหน
export const PRIORITY_OPTIONS = [
    { value: 1, label: "ด่วนมาก", style: { background: "#dc3545", color: "#fff" } },
    { value: 2, label: "ปานกลาง", style: { background: "#f0921e", color: "#fff" } },
    { value: 3, label: "ไม่ด่วน", style: { background: "#e5e7eb", color: "#374151" } },
];

// ป้ายที่ใช้โชว์บนหัวรายการ/การ์ดห้อง — เลขนอกลิสต์ให้โชว์เป็น "#เลข" ไปตามเดิม
export const priorityBadge = (priority) => {
    if (!priority) return null;
    const found = PRIORITY_OPTIONS.find((i) => i.value === Number(priority));
    return found || { value: priority, label: `ลำดับ #${priority}`, style: { background: "#212529", color: "#fff" } };
};

// "YYYY-MM-DD HH:mm" (จาก DTO) <-> ค่าใน DateTimePicker ("YYYY-MM-DDTHH:mm")
const toDateTimeInput = (value) => (value ? String(value).replace(" ", "T").slice(0, 16) : "");
const toApiDateTime = (value) => (value ? String(value).replace("T", " ") : null);

export default class InquiryModal extends Component {
  state = {
    openUid: "",     // รายการที่กางอยู่ (ทีละใบ)
    draft: null,     // ฟอร์มติดตามของรายการที่กางอยู่
    saving: "",
    rows: null,      // สำเนาที่อัปเดตหลังบันทึก (null = ใช้ของ props ตรง ๆ)
  };

  componentDidUpdate(prevProps) {
    // เปิดโมดัลใหม่ = เริ่มจากพับทุกใบเสมอ ค่าที่ยังไม่บันทึกของห้องก่อนหน้าไม่ค้าง
    if (!prevProps.show && this.props.show) {
      this.setState({ openUid: "", draft: null, rows: null });
    }
  }

  list = () => this.state.rows || this.props.inquiries || [];

  // กาง/พับ — กางแล้วดึงค่าปัจจุบันมาเป็นฟอร์ม พับแล้วทิ้ง
  toggle = (item) =>
    this.setState((prev) =>
      prev.openUid === item.uid
        ? { openUid: "", draft: null }
        : {
            openUid: item.uid,
            draft: {
              status: item.status || "ใหม่",
              priority: item.priority ?? "",
              appointment_at: toDateTimeInput(item.appointment_at),
              follow_up_at: toDateTimeInput(item.follow_up_at),
              close_reason: item.close_reason || "",
              staff_note: item.staff_note || "",
            },
          }
    );

  setField = (key, value) => this.setState((prev) => ({ draft: { ...prev.draft, [key]: value } }));

  // บันทึกงานติดตามทั้งชุดในครั้งเดียว
  save = async (item) => {
    const { draft, saving } = this.state;
    if (!draft || saving) return;

    const closing = draft.status === INQUIRY_STATUS_KEY.CLOSED;
    // ★ ฟอร์มไม่มีช่องกรอกเหตุผลปิดงานแล้ว — เก็บค่าเดิมของรายการไว้เฉย ๆ (ไม่ให้หาย) ไม่บังคับกรอกใหม่
    const closeReason = String(draft.close_reason || "").trim();

    // ★ ปิดงาน (เปลี่ยนสถานะเป็น "ปิดงาน" ครั้งแรก) backend ดึงประกาศของห้องนี้ลงจากหน้าเว็บทันที
    //   และถ้าเหตุผลคือปิดการขายได้ สถานะห้องจะเปลี่ยนเป็น ขายแล้ว/ให้เช่าแล้ว — ต้องถามก่อน
    //   รายการที่ปิดอยู่แล้ว แก้หมายเหตุแล้วกดบันทึกซ้ำ backend ไม่แตะประกาศ จึงไม่ต้องถาม
    if (closing && (item.status || INQUIRY_STATUS_KEY.NEW) !== INQUIRY_STATUS_KEY.CLOSED) {
      const confirmed = await alert(
        "",
        "warning",
        "ยืนยันปิดงาน",
        `ประกาศของห้อง ${item.address_number || this.props.roomLabel || ""} จะหยุดเผยแพร่บนหน้าเว็บทันที${closeReason ? ` · เหตุผล: ${closeReason}` : ""}`,
        "ยืนยันปิดงาน",
        "ยกเลิก"
      );
      if (!confirmed) return;
    }

    this.setState({ saving: item.uid });
    const result = await PUT("api/admin/sale-inquiries/" + item.uid, {
      status: draft.status,
      priority: draft.priority === "" ? null : Number(draft.priority),
      appointment_at: toApiDateTime(draft.appointment_at),
      follow_up_at: toApiDateTime(draft.follow_up_at),
      // เหตุผลปิดงานเก็บเฉพาะตอนปิดจริง ไม่งั้นข้อความค้างอยู่ทั้งที่งานยังเปิด · ไม่มีเหตุผลเดิม (ฟอร์มไม่มีช่องกรอกแล้ว) ให้ส่ง null
      close_reason: closing ? closeReason || null : null,
      staff_note: draft.staff_note || null,
    });
    this.setState({ saving: "" });
    if (!result?.status) return;

    this.setState((prev) => ({
      rows: this.list().map((i) => (i.uid === item.uid ? { ...i, ...(result.data || {}) } : i)),
      draft: prev.draft,
    }));

    // ป้ายแดง/ตัวเลขสรุปฝั่งหน้าหลักมาจาก backend — ให้หน้าแม่ดึงใหม่เอง
    if (this.props.onSaved) await this.props.onSaved();
    alert("", "success", "สำเร็จ", "บันทึกการติดตามเรียบร้อย");
  };

  renderItem = (item) => {
    const { openUid, draft, saving } = this.state;
    const isOpen = openUid === item.uid;
    const form = isOpen ? draft || {} : {};
    const status = item.status || INQUIRY_STATUS_KEY.NEW;
    // ★ สีแถวใช้กติกาเดียวกับการ์ดห้องในหน้านัดหมาย : เกินนัด = แดง, ใกล้ถึง = เหลือง
    const urgency = appointmentUrgency(item);
    const urgencyStyle = URGENCY_STYLE[urgency];

    return (
      <div
        key={item.uid}
        className="border rounded-4 mb-3 overflow-hidden"
        style={urgencyStyle?.card || (status === INQUIRY_STATUS_KEY.NEW ? { borderColor: "#dc3545" } : undefined)}
      >
        {/* ---------------- หัวรายการ (กดเพื่อกาง) ---------------- */}
        <div className="d-flex justify-content-between align-items-center gap-2 p-3 pointer" onClick={() => this.toggle(item)}>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <div>
              <div className="fw-bold">
                {item.full_name}
                <span className="ms-2 px-2 py-1 rounded-pill text-12" style={INQUIRY_STATUS_STYLE[status]}>
                  {status}
                </span>
                {!!urgencyStyle && (
                  <span className="ms-2 px-2 py-1 rounded-pill text-12 fw-bold" style={urgencyStyle.badge}>
                    {urgencyStyle.label}
                  </span>
                )}
              </div>
              <small className="text-secondary">
                ส่งเมื่อ {formatSaleDateTime(item.created_at) || "-"}
                {item.appointment_at ? ` · นัดชม ${formatSaleDateTime(item.appointment_at)}` : ""}
                {item.follow_up_at ? ` · ติดตาม ${formatSaleDateTime(item.follow_up_at)}` : ""}
              </small>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            {/* กดโทรจากมือถือได้เลย — กดปุ่มนี้ต้องไม่พับ/กางรายการ */}
            <a
              className="btn btn-sm btn-success px-3"
              href={`tel:${String(item.phone || "").replace(/[^\d+]/g, "")}`}
              onClick={(e) => e.stopPropagation()}
            >
              โทร {item.phone || "-"}
            </a>
            <span className="icon text-12">{isOpen ? "" : ""}</span>
          </div>
        </div>

        {isOpen && (
          <div className="border-top p-3 bg-light">
            {/* ============ ส่วนที่ 1 : ข้อมูลที่ผู้สนใจกรอกมา (อ่านอย่างเดียว) ============ */}
            <div className="fw-bold mb-2">ข้อมูลจากผู้สนใจ</div>
            <div className="row bg-white rounded-3 p-3 mb-3">
              {/* ★ ป้ายชื่อ/อีเมลใช้ถ้อยคำเดิมของหน้านัดหมาย ("ชื่อ-สกุล/ชื่อเล่น", "อีเมล์/Line")
                  ผู้สนใจบางคนกรอกไลน์ไอดีมาในช่องอีเมล ป้ายจึงต้องครอบทั้งสองอย่าง */}
              {[
                ["ชื่อ-สกุล/ชื่อเล่น", item.full_name, "name"],
                ["ห้องที่สนใจ", item.address_number, "room"],
                ["ความสนใจ", item.interest_type, "interest"],
                ["เบอร์ติดต่อ", item.phone, "phone"],
                ["อีเมล์/Line", item.email, "email"],
                ["วันที่สะดวก", formatSaleDate(item.visit_date), "visit-date"],
                ["เวลาที่สะดวก", item.visit_time, "visit-time"],
              ].map(([label, value, key]) => (
                <div className="col-12 col-sm-6 d-flex mb-2" key={label}>
                  <div className="fw-bold wpx-100 flex-shrink-0" data-testid={`inquiry-detail-${key}-label`}>
                    {label}
                  </div>
                  <span data-testid={`inquiry-detail-${key}-value`}>{value || "-"}</span>
                </div>
              ))}
              <div className="col-12 d-flex">
                <div className="fw-bold wpx-100 flex-shrink-0">ข้อความ</div>
                <span>{item.message || "-"}</span>
              </div>
            </div>

            {/* ============ ส่วนที่ 2 : งานติดตามของผู้ดูแล ============ */}
            <div className="fw-bold mb-2">การติดตามของผู้ดูแล</div>
            <div className="bg-white rounded-3 p-3">
              <div className="mb-3">
                <label className="fw-bold mb-2 d-block">สถานะความคืบหน้า</label>
                <div className="d-flex flex-wrap gap-2">
                  {INQUIRY_STATUS.map((option) => (
                    <button
                      key={option}
                      type="button"
                      className={`btn btn-sm px-3 ${form.status === option ? "btn-success" : "btn-outline-success"}`}
                      onClick={() => this.setField("status", option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="fw-bold mb-2">วันเวลานัดชมห้อง</label>
                  {/* ★ ใช้ DateTimePicker กลาง ไม่ใช้ datetime-local — native แสดงวันที่ตาม locale เบราว์เซอร์ */}
                  <DateTimePicker
                    value={form.appointment_at || ""}
                    onChange={(v) => this.setField("appointment_at", v)}
                  />
                  <small className="text-secondary">นัดที่ตกลงกับผู้สนใจแล้ว</small>
                </div>
                <div className="col-12 col-md-6">
                  <label className="fw-bold mb-2">ติดตามครั้งถัดไป</label>
                  <DateTimePicker
                    value={form.follow_up_at || ""}
                    onChange={(v) => this.setField("follow_up_at", v)}
                  />
                  <small className="text-secondary">ใช้เรียงคิวว่าต้องตามใครก่อน</small>
                </div>

                <div className="col-12">
                  <label className="fw-bold mb-2">ข้อความจากผู้ดูแล (บันทึกภายใน)</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="เช่น โทรแล้วไม่รับ นัดโทรใหม่พรุ่งนี้เช้า"
                    value={form.staff_note || ""}
                    onChange={(e) => this.setField("staff_note", e.target.value)}
                  />
                  <small className="text-secondary">ไม่แสดงบนหน้าเว็บสาธารณะ</small>
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
                <small className="text-secondary">
                  {item.handled_by ? `แก้ล่าสุดโดย ${item.handled_by} · ${formatSaleDateTime(item.handled_at) || "-"}` : "ยังไม่มีใครติดตาม"}
                </small>
                {/* ★ ไม่มีสิทธิ์ sale-inquiries.update = กดแล้วโดน 403 จึงไม่แสดงปุ่ม (ยังเปิดดูข้อมูลได้) */}
                {hasPermission("sale-inquiries.update") ? (
                  <button className="btn btn-success px-4" disabled={!!saving} onClick={() => this.save(item)}>
                    {saving === item.uid ? "กำลังบันทึก..." : "บันทึกการติดตาม"}
                  </button>
                ) : (
                  <small className="text-secondary">ไม่มีสิทธิ์แก้ไขการติดตาม</small>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  render() {
    const { show, roomLabel, onClose } = this.props;
    const rows = this.list();

    return (
      <Modal show={show} size="lg" onHide={onClose}>
        <Modal.Header closeButton>
          <div>
            <h5 className="fw-bold mb-0">นัดหมายห้อง {roomLabel || ""}</h5>
            <small className="text-secondary">คำขอนัดดูห้องที่ส่งมาจากหน้าเว็บประกาศขาย/เช่า</small>
          </div>
        </Modal.Header>
        <Modal.Body>
          {!rows.length ? (
            <div className="text-center text-secondary py-5">ยังไม่มีผู้ติดต่อเข้ามา</div>
          ) : (
            <>
              {/* สรุปคิวงานของห้องนี้ — เห็นตั้งแต่บนสุดว่าเหลือของต้องตามกี่ราย */}
              <div className="d-flex flex-wrap gap-2 mb-3">
                {INQUIRY_STATUS.map((status) => {
                  const count = rows.filter((i) => (i.status || INQUIRY_STATUS_KEY.NEW) === status).length;
                  if (!count) return null;
                  return (
                    <span key={status} className="px-3 py-1 rounded-pill text-12" style={INQUIRY_STATUS_STYLE[status]}>
                      {status} {count}
                    </span>
                  );
                })}
              </div>

              {rows.map(this.renderItem)}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="py-1">
          <button className="btn btn-outline-success px-4" onClick={onClose}>
            ปิดหน้าต่าง
          </button>
        </Modal.Footer>
      </Modal>
    );
  }
}
